/**
 * TaxDashboardPage
 * 세무 대시보드 - 연간 매출/매입/세금 현황
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { TaxRepository } from '../../repositories/TaxRepository';
import { formatKRW } from '../../models/Tax';

const taxRepository = new TaxRepository();

interface MonthData {
  month: number;
  expense_amount: number;
  expense_vat: number;
  expense_total: number;
  expense_count: number;
  revenue_amount: number;
  revenue_vat: number;
  revenue_total: number;
  revenue_count: number;
  invoice_sales_tax: number;
  invoice_sales_count: number;
  invoice_purchase_tax: number;
  invoice_purchase_count: number;
}

interface DashboardData {
  year: number;
  months: MonthData[];
  ytd: {
    expense_total: number;
    expense_vat: number;
    revenue_total: number;
    revenue_vat: number;
    invoice_sales_tax: number;
    invoice_purchase_tax: number;
  };
}

export const TaxDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await taxRepository.getDashboard(year);
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : '대시보드 로드 실패');
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const yearOptions = Array.from({ length: 4 }, (_, i) => now.getFullYear() - i);

  // 바 차트용 최대값 계산
  const maxTotal = data
    ? Math.max(...data.months.map(m => Math.max(m.revenue_total, m.expense_total)), 1)
    : 1;

  // 예상 부가세 (매출세액 - 매입세액)
  const estimatedVat = data
    ? (data.ytd.revenue_vat + data.ytd.invoice_sales_tax) - (data.ytd.expense_vat + data.ytd.invoice_purchase_tax)
    : 0;

  return (
    <div className="space-y-6">
      {/* 헤더 */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">세무 대시보드</h1>
        <select
          value={year}
          onChange={(e) => setYear(Number(e.target.value))}
          className="px-3 py-2 border border-gray-300 rounded-md text-sm"
        >
          {yearOptions.map((y) => (
            <option key={y} value={y}>{y}년</option>
          ))}
        </select>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {loading ? (
        <div className="text-center py-12 text-gray-500">불러오는 중...</div>
      ) : data ? (
        <>
          {/* YTD 요약 카드 */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div
              className="bg-white rounded-lg shadow p-4 cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => navigate('/admin/tax/revenues')}
            >
              <p className="text-sm text-gray-500">연간 매출 합계</p>
              <p className="text-2xl font-bold text-green-600">{formatKRW(data.ytd.revenue_total)}</p>
              <p className="text-xs text-gray-400 mt-1">부가세: {formatKRW(data.ytd.revenue_vat)}</p>
            </div>
            <div
              className="bg-white rounded-lg shadow p-4 cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => navigate('/admin/tax/expenses')}
            >
              <p className="text-sm text-gray-500">연간 매입/경비 합계</p>
              <p className="text-2xl font-bold text-orange-600">{formatKRW(data.ytd.expense_total)}</p>
              <p className="text-xs text-gray-400 mt-1">부가세: {formatKRW(data.ytd.expense_vat)}</p>
            </div>
            <div
              className="bg-white rounded-lg shadow p-4 cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => navigate('/admin/tax/invoices')}
            >
              <p className="text-sm text-gray-500">세금계산서 세액</p>
              <p className="text-lg font-bold text-gray-900">
                매출: {formatKRW(data.ytd.invoice_sales_tax)}
              </p>
              <p className="text-lg font-bold text-gray-900">
                매입: {formatKRW(data.ytd.invoice_purchase_tax)}
              </p>
            </div>
            <div
              className="bg-white rounded-lg shadow p-4 cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => navigate('/admin/tax/vat')}
            >
              <p className="text-sm text-gray-500">예상 부가세 납부액</p>
              <p className={`text-2xl font-bold ${estimatedVat >= 0 ? 'text-red-600' : 'text-blue-600'}`}>
                {estimatedVat >= 0 ? formatKRW(estimatedVat) : `-${formatKRW(Math.abs(estimatedVat))}`}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                {estimatedVat >= 0 ? '납부 예상' : '환급 예상'}
              </p>
            </div>
          </div>

          {/* 월별 바 차트 */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">월별 매출 vs 매입</h2>
            <div className="space-y-3">
              {data.months.map((m) => (
                <div key={m.month} className="flex items-center gap-3">
                  <span className="text-sm text-gray-600 w-8 text-right">{m.month}월</span>
                  <div className="flex-1 space-y-1">
                    {/* 매출 바 */}
                    <div className="flex items-center gap-2">
                      <div
                        className="h-4 bg-green-400 rounded-sm transition-all"
                        style={{ width: `${(m.revenue_total / maxTotal) * 100}%`, minWidth: m.revenue_total > 0 ? '4px' : '0' }}
                      />
                      {m.revenue_total > 0 && (
                        <span className="text-xs text-green-700 whitespace-nowrap">
                          {m.revenue_total.toLocaleString()}
                        </span>
                      )}
                    </div>
                    {/* 매입 바 */}
                    <div className="flex items-center gap-2">
                      <div
                        className="h-4 bg-orange-400 rounded-sm transition-all"
                        style={{ width: `${(m.expense_total / maxTotal) * 100}%`, minWidth: m.expense_total > 0 ? '4px' : '0' }}
                      />
                      {m.expense_total > 0 && (
                        <span className="text-xs text-orange-700 whitespace-nowrap">
                          {m.expense_total.toLocaleString()}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex gap-6 mt-4 text-xs text-gray-500">
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 bg-green-400 rounded-sm" />
                <span>매출</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 bg-orange-400 rounded-sm" />
                <span>매입/경비</span>
              </div>
            </div>
          </div>

          {/* 월별 상세 테이블 */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">월</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">매출 합계</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">매출 부가세</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">매입 합계</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">매입 부가세</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">손익</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {data.months.map((m) => {
                    const profit = m.revenue_total - m.expense_total;
                    return (
                      <tr key={m.month} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{m.month}월</td>
                        <td className="px-4 py-3 text-sm text-green-600 text-right">{m.revenue_total.toLocaleString()}</td>
                        <td className="px-4 py-3 text-sm text-gray-500 text-right">{m.revenue_vat.toLocaleString()}</td>
                        <td className="px-4 py-3 text-sm text-orange-600 text-right">{m.expense_total.toLocaleString()}</td>
                        <td className="px-4 py-3 text-sm text-gray-500 text-right">{m.expense_vat.toLocaleString()}</td>
                        <td className={`px-4 py-3 text-sm font-medium text-right ${profit >= 0 ? 'text-green-700' : 'text-red-600'}`}>
                          {profit.toLocaleString()}
                        </td>
                      </tr>
                    );
                  })}
                  {/* 합계 행 */}
                  <tr className="bg-gray-50 font-bold">
                    <td className="px-4 py-3 text-sm text-gray-900">합계</td>
                    <td className="px-4 py-3 text-sm text-green-700 text-right">{data.ytd.revenue_total.toLocaleString()}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 text-right">{data.ytd.revenue_vat.toLocaleString()}</td>
                    <td className="px-4 py-3 text-sm text-orange-700 text-right">{data.ytd.expense_total.toLocaleString()}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 text-right">{data.ytd.expense_vat.toLocaleString()}</td>
                    <td className={`px-4 py-3 text-sm text-right ${data.ytd.revenue_total - data.ytd.expense_total >= 0 ? 'text-green-700' : 'text-red-600'}`}>
                      {(data.ytd.revenue_total - data.ytd.expense_total).toLocaleString()}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
};
